import { z } from 'zod';

export const CARE_TEAM_RULE_SCOPES = ['default_pool'] as const;
export type CareTeamRuleScope = (typeof CARE_TEAM_RULE_SCOPES)[number];

export const CARE_TEAM_RULE_KINDS = ['programme'] as const;
export type CareTeamRuleKind = (typeof CARE_TEAM_RULE_KINDS)[number];

const careTeamRuleKindSchema = z.enum(CARE_TEAM_RULE_KINDS);

export function parseCareTeamRuleKind(value: string): CareTeamRuleKind | null {
  const parsed = careTeamRuleKindSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function noDuplicateKeys(keys: string[]): boolean {
  return new Set(keys).size === keys.length;
}

const programmeRuleShape = {
  kind: z.literal('programme'),
  programmeKeys: z
    .array(z.string().min(1))
    .min(1)
    .refine(noDuplicateKeys, { message: 'programme keys must be unique' }),
} as const;

/** Request bodies — reject unknown keys (ADR-135). */
export const programmeRuleRequestSchema = z.object(programmeRuleShape).strict();

/** Client-parsed responses — forward-compatible (ADR-135 Rule 1). */
export const programmeRuleSchema = z.object(programmeRuleShape);

export const careTeamRuleRequestSchema = z.discriminatedUnion('kind', [programmeRuleRequestSchema]);
export const careTeamRuleSchema = z.discriminatedUnion('kind', [programmeRuleSchema]);
export type CareTeamRule = z.infer<typeof careTeamRuleSchema>;

/** Stored jsonb shape — the rule variant without the discriminant `kind` column. */
export type CareTeamRuleConfig = Omit<CareTeamRule, 'kind'>;

export function ruleFromRow(kind: CareTeamRuleKind, config: unknown): CareTeamRule | null {
  const parsed = careTeamRuleSchema.safeParse({ kind, ...asRecord(config) });
  return parsed.success ? parsed.data : null;
}

export function rowFromRule(rule: CareTeamRule): {
  kind: CareTeamRuleKind;
  config: CareTeamRuleConfig;
} {
  const { kind, ...config } = rule;
  return { kind, config };
}

export type CareTeamRuleMemberContext = {
  mainProgrammeKey: string | null;
};

export function matchCareTeamRule(rule: CareTeamRule, ctx: CareTeamRuleMemberContext): boolean {
  if (rule.kind === 'programme') {
    if (ctx.mainProgrammeKey == null) return false;
    return rule.programmeKeys.includes(ctx.mainProgrammeKey);
  }
  return false;
}

export function rulesAcceptMember(
  rules: readonly CareTeamRule[],
  ctx: CareTeamRuleMemberContext,
): boolean {
  if (rules.length === 0) return true;
  return rules.every((rule) => matchCareTeamRule(rule, ctx));
}

/** Order-insensitive equality for default-pool rule sets stored as jsonb arrays. */
export function careTeamRulesEqual(
  a: readonly CareTeamRule[],
  b: readonly CareTeamRule[],
): boolean {
  const serialise = (rules: readonly CareTeamRule[]) =>
    JSON.stringify(
      [...rules].sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right))),
    );
  return serialise(a) === serialise(b);
}

export function partitionByRules<T extends { rules: readonly CareTeamRule[] }>(
  candidates: readonly T[],
  ctx: CareTeamRuleMemberContext,
): { matched: T[]; unrestricted: T[]; unmatched: T[] } {
  const matched: T[] = [];
  const unrestricted: T[] = [];
  const unmatched: T[] = [];
  for (const candidate of candidates) {
    if (candidate.rules.length === 0) {
      unrestricted.push(candidate);
    } else if (rulesAcceptMember(candidate.rules, ctx)) {
      matched.push(candidate);
    } else {
      unmatched.push(candidate);
    }
  }
  return { matched, unrestricted, unmatched };
}

function asRecord(value: unknown): Record<string, unknown> {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value));
}
